/**
 * WarningAcknowledgementList — every warning line on a run, each with its own
 * "Acknowledge" (warning acknowledgement, 2026-10-06). Shared by ora's drawer,
 * ora's run record and the Outlook add-in's drawer: one component, one look (one
 * UI standard). Since story 28633 (2026-10-08) it renders on a LIVE run too —
 * the live drawer bodies' `warningLines` slot — so a line can be acknowledged
 * while the person is dealing with it. Wherever the API publishes `warning_items`, it
 * replaces the "Ended early — …" banner, the per-warning banners and (R57/R59)
 * the legacy `metadata.warning` banner; where it does not, the host keeps its old
 * banners. Lines render in the server's order (early_exit, metadata_warning,
 * then each `*_warning` key). Only `early_exit` gets the "Ended early — " prefix.
 *
 * An acknowledged line stays on screen, de-emphasised, naming who and when. A
 * pressed line reads "Acknowledging…" until the server answers — never
 * "Acknowledged" before (R50). No button when the caller may not acknowledge
 * (the server's `can_acknowledge`): viewing as someone, or a substrate that
 * predates live acknowledgement and still says false until the run finishes.
 *
 * Presentational: all state comes from `useWarningAcknowledgements`.
 */
import { useEffect, useRef } from "react";
import type { WarningAckState } from "../hooks/useWarningAcknowledgements";
import {
  ACKNOWLEDGE_LABEL,
  ACKNOWLEDGING_LABEL,
  EARLY_EXIT_PREFIX,
  acknowledgedByLine,
  type RunWarningItem,
} from "../warningAcknowledgement";

export interface WarningAcknowledgementListProps {
  ack: Pick<WarningAckState, "items" | "canAcknowledge" | "pendingIds" | "errorById" | "acknowledge">;
}

type ListAck = WarningAcknowledgementListProps["ack"];

function WarningLine({ item, ack }: { item: RunWarningItem; ack: ListAck }) {
  const pending = ack.pendingIds.has(item.id);
  const error = ack.errorById[item.id];
  const buttonHadFocus = useRef(false);
  const byRef = useRef<HTMLParagraphElement>(null);
  // On confirmation, move focus to the "Acknowledged by" line, but only if the button had it.
  useEffect(() => {
    if (item.acknowledged && buttonHadFocus.current) {
      buttonHadFocus.current = false;
      byRef.current?.focus();
    }
  }, [item.acknowledged]);
  return (
    <li
      data-testid={`warning-ack-item-${item.id}`}
      data-acknowledged={item.acknowledged ? "true" : "false"}
      aria-busy={pending ? "true" : undefined}
      className={
        item.acknowledged
          ? "rounded-md border border-primary-gray-light-200 bg-primary-gray-light-50 px-3 py-2"
          : "rounded-md border border-primary-warning-200 bg-primary-warning-50 px-3 py-2"
      }
    >
      <p
        className={
          item.acknowledged ? "text-xs text-primary-gray-light-600" : "text-xs text-primary-warning-700"
        }
      >
        {item.kind === "early_exit" ? <span className="font-semibold">{EARLY_EXIT_PREFIX}</span> : null}
        {item.message}
      </p>
      {item.acknowledged ? (
        <p
          ref={byRef}
          tabIndex={-1}
          className="mt-1 text-2xs text-primary-gray-light-500"
          data-testid={`warning-ack-by-${item.id}`}
        >
          {acknowledgedByLine(item)}
        </p>
      ) : ack.canAcknowledge ? (
        <button
          type="button"
          // aria-disabled (not disabled) while pending keeps keyboard focus; a repeat press is ignored.
          onClick={() => {
            if (!pending) void ack.acknowledge(item.id);
          }}
          onFocus={() => {
            buttonHadFocus.current = true;
          }}
          onBlur={() => {
            buttonHadFocus.current = false;
          }}
          aria-disabled={pending ? "true" : undefined}
          aria-label={`${pending ? ACKNOWLEDGING_LABEL : ACKNOWLEDGE_LABEL}: ${item.message}`}
          className="mt-1 border-b-2 border-sunrise text-2xs font-semibold text-primary-gray-light-900 aria-disabled:cursor-default aria-disabled:opacity-60"
          data-testid={`warning-ack-button-${item.id}`}
        >
          {pending ? ACKNOWLEDGING_LABEL : ACKNOWLEDGE_LABEL}
        </button>
      ) : null}
      {error ? (
        <p
          role="alert"
          className="mt-1 text-2xs text-primary-error-700"
          data-testid={`warning-ack-error-${item.id}`}
        >
          {error}
        </p>
      ) : null}
    </li>
  );
}

export function WarningAcknowledgementList({ ack }: WarningAcknowledgementListProps) {
  if (!ack.items || ack.items.length === 0) return null;
  return (
    <ul className="mb-1 flex flex-col gap-1" data-testid="warning-ack-list" aria-label="Warnings">
      {ack.items.map((item) => (
        <WarningLine key={item.id} item={item} ack={ack} />
      ))}
    </ul>
  );
}

export default WarningAcknowledgementList;
